import React, { useEffect, useMemo, useState, useRef } from "react";
import "./UserGame.css";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getManagerSettings } from "../../functions/getManagerSettings";
import { QRCodeCanvas } from "qrcode.react";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";

export default function UserGame() {
  const location = useLocation();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [lobbyPin, setLobbyPin] = useState("");
  const [lobbyLink, setLobbyLink] = useState("");
  const [loading, setLoading] = useState(false);
  const qrRef = useRef(null);
  const demoBypassAlertRef = useRef(false);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  const isDemoBypass = useMemo(() => {
    const sid = String(storedUserData.sessionId ?? "").trim().toLowerCase();
    return sid === "demobypass";
  }, [storedUserData.sessionId]);

  const plansPageUrl = useMemo(() => {
    const base = String(process.env.REACT_APP_BASE_URL || "https://staging.extramileplay.com").replace(
      /\/+$/,
      ""
    );
    return `${base}/plans`;
  }, []);

  const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
  const themeName = "guess_me";

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData]);

  const lobbyName = useMemo(() => {
    const st = location.state || {};
    if (st?.lobby_name) return String(st.lobby_name);
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      if (cached?.lobby_name) return String(cached.lobby_name);
    } catch {
      /* ignore */
    }
    return "";
  }, [location.state]);

  const pinDisplay = useMemo(() => {
    const p = String(lobbyPin || "").trim();
    if (!p) return loading ? "…" : "—";
    if (/^\d+$/.test(p)) {
      return p.length <= 4 ? p.padStart(4, "0") : p;
    }
    return p;
  }, [lobbyPin, loading]);

  useEffect(() => {
    document.documentElement.classList.add("usergame-static-bg");
    document.body.classList.add("usergame-static-bg");
    return () => {
      document.documentElement.classList.remove("usergame-static-bg");
      document.body.classList.remove("usergame-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!isDemoBypass || demoBypassAlertRef.current) return;
    demoBypassAlertRef.current = true;
    let cancelled = false;
    Swal.fire({
      title: "This is a live game.",
      text: "Subscribe to a plan to access and play the full game experience.",
      icon: "info",
      confirmButtonText: "View plans",
      confirmButtonColor: "#243b6b",
      allowOutsideClick: false,
    }).then(() => {
      if (!cancelled) window.location.href = plansPageUrl;
    });
    return () => {
      cancelled = true;
    };
  }, [isDemoBypass, plansPageUrl]);

  useEffect(() => {
    if (isDemoBypass || !tokenForApi || !uid) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await getManagerSettings({ token: tokenForApi, userId: uid, themeName });
        if (cancelled) return;
        setLobbyPin(resp?.lobby_pin != null ? String(resp.lobby_pin) : "");
        setLobbyLink(resp?.lobby_link != null ? String(resp.lobby_link) : "");
      } catch (e) {
        if (!cancelled) {
          await Swal.fire("Error", e?.message || "Failed to load lobby", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDemoBypass, tokenForApi, uid, themeName]);

  const copyLink = async () => {
    if (!lobbyLink) return;
    try {
      await navigator.clipboard.writeText(lobbyLink);
      await Swal.fire("Copied", "Lobby link copied to clipboard", "success");
    } catch {
      await Swal.fire("Copy failed", "Please copy manually", "error");
    }
  };

  const qrDownloadFilename = useMemo(() => {
    const slug = String(lobbyName || "equity-taboo")
      .trim()
      .replace(/[^\w\-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 48);
    const pin = String(lobbyPin || "")
      .trim()
      .replace(/\D/g, "")
      .padStart(5, "0")
      .slice(-5);
    const base = slug || "equity-taboo";
    return pin ? `Equity-Taboo-${base}-PIN-${pin}.png` : `Equity-Taboo-${base}-QR.png`;
  }, [lobbyName, lobbyPin]);

  const downloadQR = () => {
    if (!qrRef.current) return;
    try {
      const canvas = qrRef.current.querySelector("canvas");
      if (!canvas) return;
      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = qrDownloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      Swal.fire("Downloaded", "QR code downloaded successfully", "success");
    } catch {
      Swal.fire("Error", "Failed to download QR code", "error");
    }
  };

  const handleStart = () => {
    if (isDemoBypass) return;
    if (!lobbyName) return;
    navigate("/joinedLobby", { replace: true, state: { lobby_name: lobbyName, backTo: "/usergame" } });
  };

  return (
    <main className="ug-page" aria-label="Lobby share">
      <div className="ug-confetti" aria-hidden="true" />
      <div className="ug-inner">
        <div className="ug-duo">
          <div className="ug-duo-col">
            <section className="ug-panel ug-panel--create" aria-labelledby="ug-create-heading">
              <h2 id="ug-create-heading" className="ug-panel-title">
                CREATE
              </h2>
              <div className="ug-panel-box" ref={qrRef}>
                {lobbyLink ? (
                  <QRCodeCanvas value={lobbyLink} size={180} level="M" includeMargin={false} />
                ) : (
                  <div className="ug-qr-placeholder" aria-hidden />
                )}
              </div>
              <p className="ug-panel-hint">scan to join</p>
            </section>
            <button
              type="button"
              className="ug-action ug-action--create"
              onClick={downloadQR}
              disabled={!lobbyLink || loading}
            >
              Download Qr
            </button>
          </div>

          <div className="ug-duo-col">
            <section className="ug-panel ug-panel--join" aria-labelledby="ug-join-heading">
              <h2 id="ug-join-heading" className="ug-panel-title">
                JOIN
              </h2>
              <div className="ug-panel-box">
                <span className="ug-pin-code" aria-label={`Lobby PIN ${pinDisplay}`}>
                  {pinDisplay}
                </span>
              </div>
            </section>
            <button
              type="button"
              className="ug-action ug-action--join"
              onClick={() => void copyLink()}
              disabled={!lobbyLink || loading}
            >
              Copy Link
            </button>
          </div>
        </div>

        <div className="ug-start-wrap">
          <button
            type="button"
            className="ug-start"
            onClick={handleStart}
            disabled={isDemoBypass || !lobbyName || loading}
          >
            Start Game
          </button>
        </div>
      </div>
    </main>
  );
}
